/**
 * Valida email
 * @param {string} email
 * @returns {boolean}
 */
export const isValidEmail = (email) => {
  if (!email) return false;
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Valida telefone brasileiro
 * @param {string} phone
 * @returns {boolean}
 */
export const isValidPhone = (phone) => {
  if (!phone) return false;
  
  const cleaned = phone.replace(/\D/g, '');
  
  // Aceita 10 ou 11 dígitos (com ou sem 9 no celular)
  return cleaned.length === 10 || cleaned.length === 11;
};

/**
 * Valida CPF
 * @param {string} cpf
 * @returns {boolean}
 */
export const isValidCPF = (cpf) => {
  if (!cpf) return false;
  
  const cleaned = cpf.replace(/\D/g, '');
  
  if (cleaned.length !== 11) return false;
  
  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1+$/.test(cleaned)) return false;
  
  // Valida primeiro dígito verificador
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleaned.charAt(i)) * (10 - i);
  }
  let digit = 11 - (sum % 11);
  if (digit >= 10) digit = 0;
  if (digit !== parseInt(cleaned.charAt(9))) return false;
  
  // Valida segundo dígito verificador
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleaned.charAt(i)) * (11 - i);
  }
  digit = 11 - (sum % 11);
  if (digit >= 10) digit = 0;
  if (digit !== parseInt(cleaned.charAt(10))) return false;
  
  return true;
};

/**
 * Valida CNPJ
 * @param {string} cnpj
 * @returns {boolean}
 */
export const isValidCNPJ = (cnpj) => {
  if (!cnpj) return false;
  
  const cleaned = cnpj.replace(/\D/g, '');
  
  if (cleaned.length !== 14) return false;
  
  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1+$/.test(cleaned)) return false;
  
  // Valida primeiro dígito verificador
  let length = cleaned.length - 2;
  let numbers = cleaned.substring(0, length);
  const digits = cleaned.substring(length);
  let sum = 0;
  let pos = length - 7;
  
  for (let i = length; i >= 1; i--) {
    sum += parseInt(numbers.charAt(length - i)) * pos--;
    if (pos < 2) pos = 9;
  }
  
  let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(0))) return false;
  
  // Valida segundo dígito verificador
  length = length + 1;
  numbers = cleaned.substring(0, length);
  sum = 0;
  pos = length - 7;
  
  for (let i = length; i >= 1; i--) {
    sum += parseInt(numbers.charAt(length - i)) * pos--;
    if (pos < 2) pos = 9;
  }
  
  result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(1))) return false;
  
  return true;
};

/**
 * Valida senha forte
 * @param {string} password
 * @returns {Object} - { valid: boolean, errors: string[] }
 */
export const validatePassword = (password) => {
  const errors = [];
  
  if (!password) {
    errors.push('Senha é obrigatória');
    return { valid: false, errors };
  }
  
  if (password.length < 8) {
    errors.push('Senha deve ter no mínimo 8 caracteres');
  }
  
  if (!/[A-Z]/.test(password)) {
    errors.push('Senha deve conter pelo menos uma letra maiúscula');
  }
  
  if (!/[a-z]/.test(password)) {
    errors.push('Senha deve conter pelo menos uma letra minúscula');
  }
  
  if (!/[0-9]/.test(password)) {
    errors.push('Senha deve conter pelo menos um número');
  }
  
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    errors.push('Senha deve conter pelo menos um caractere especial');
  }
  
  return {
    valid: errors.length === 0,
    errors,
  };
};

/**
 * Valida campo obrigatório
 * @param {any} value
 * @param {string} fieldName
 * @returns {string|null} - Mensagem de erro ou null
 */
export const validateRequired = (value, fieldName = 'Campo') => {
  if (!value || (typeof value === 'string' && value.trim() === '')) {
    return `${fieldName} é obrigatório`;
  }
  return null;
};

/**
 * Valida tamanho mínimo
 * @param {string} value
 * @param {number} min
 * @param {string} fieldName
 * @returns {string|null}
 */
export const validateMinLength = (value, min, fieldName = 'Campo') => {
  if (value && value.length < min) {
    return `${fieldName} deve ter no mínimo ${min} caracteres`;
  }
  return null;
};

/**
 * Valida tamanho máximo
 * @param {string} value
 * @param {number} max
 * @param {string} fieldName
 * @returns {string|null}
 */
export const validateMaxLength = (value, max, fieldName = 'Campo') => {
  if (value && value.length > max) {
    return `${fieldName} deve ter no máximo ${max} caracteres`;
  }
  return null;
};

/**
 * Valida URL
 * @param {string} url
 * @returns {boolean}
 */
export const isValidURL = (url) => {
  if (!url) return false;
  
  try {
    new URL(url);
    return true;
  } catch (err) {
    return false;
  }
};

/**
 * Valida número
 * @param {any} value
 * @returns {boolean}
 */
export const isValidNumber = (value) => {
  return !isNaN(parseFloat(value)) && isFinite(value);
};

/**
 * Valida data
 * @param {string} date
 * @returns {boolean}
 */
export const isValidDate = (date) => {
  if (!date) return false;
  const d = new Date(date);
  return d instanceof Date && !isNaN(d.getTime());
};

/**
 * Valida formulário completo
 * @param {Object} formData - Dados do formulário
 * @param {Object} rules - Regras de validação
 * @returns {Object} - { valid: boolean, errors: Object }
 */
export const validateForm = (formData, rules) => {
  const errors = {};
  let valid = true;
  
  Object.keys(rules).forEach((field) => {
    const fieldRules = rules[field];
    const value = formData[field];
    
    if (fieldRules.required) {
      const error = validateRequired(value, fieldRules.label || field);
      if (error) {
        errors[field] = error;
        valid = false;
        return;
      }
    }
    
    if (fieldRules.email && value) {
      if (!isValidEmail(value)) {
        errors[field] = 'Email inválido';
        valid = false;
        return;
      }
    }
    
    if (fieldRules.phone && value) {
      if (!isValidPhone(value)) {
        errors[field] = 'Telefone inválido';
        valid = false;
        return;
      }
    }
    
    if (fieldRules.minLength && value) {
      const error = validateMinLength(value, fieldRules.minLength, fieldRules.label || field);
      if (error) {
        errors[field] = error;
        valid = false;
        return;
      }
    }
    
    if (fieldRules.maxLength && value) {
      const error = validateMaxLength(value, fieldRules.maxLength, fieldRules.label || field);
      if (error) {
        errors[field] = error;
        valid = false;
        return;
      }
    }
    
    if (fieldRules.custom && typeof fieldRules.custom === 'function') {
      const error = fieldRules.custom(value, formData);
      if (error) {
        errors[field] = error;
        valid = false;
      }
    }
  });
  
  return { valid, errors };
};